// src/components/pages/FaqPage/FaqPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { V } from '../../services/obfyscatedStrings';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import Loader from '../../widgets/Loader/Loader';
import { BackIcon } from '../../assets/images/BackIcon';
import './FaqPage.scss';

interface FaqItem {
    id: string;
    question: string;
    answer: string | string[];
    category: string;
}

function FaqPage() {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [openItems, setOpenItems] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const categories = [
        { id: 'all', name: 'Все', icon: '📋' },
        { id: 'connection', name: 'Подключение', icon: '🔌' },
        { id: 'payment', name: 'Оплата', icon: '💳' },
        { id: 'account', name: 'Аккаунт', icon: '👤' },
        { id: 'promo', name: 'Бонусы', icon: '🎁' },
    ];

    const faqItems: FaqItem[] = [
        {
            id: 'connection-1',
            question: `Как подключить ${V()} на дополнительные устройства`,
            answer: 'Перейдите в раздел «Устройства», выберите устройство, которое ожидает настройки, затем нажмите «Настроить устройство». После этого выберите нужный протокол и следуйте дальнейшим инструкциям на экране.',
            category: 'connection'
        },
        {
            id: 'connection-2',
            question: `Перестал подключаться ${V()}`,
            answer: [
                'Попробуйте сменить сервер.',
                'Для Vless выберите другой сервер из списка.',
                'Для WireGuard и AmneziaWG перейдите в раздел «Устройства», удалите текущее устройство и создайте его заново, выбрав другой сервер.',
                'Если проблема сохраняется, проверьте подключение к интернету и повторите попытку позже.',
                'Если проблема наблюдается длительное время, обратитесь в поддержку.'
            ],
            category: 'connection'
        },
        {
            id: 'connection-3',
            question: 'Что делать, если пишет encrypted в Happ?',
            answer: 'Если у вас пишет encrypted в Happ, вам необходимо обновить приложение до самой свежей версии.',
            category: 'connection'
        },
        {
            id: 'connection-4',
            question: 'В чем разница между Proxy и TUN на PC?',
            answer: 'При подключении через PC у вас есть 2 варианта: Proxy или TUN. Proxy направляет трафик через VPN только в браузере, а приложения и игры не затрагивает. TUN перенаправляет через VPN весь трафик устройства.',
            category: 'connection'
        },
        {
            id: 'payment-1',
            question: 'Как купить подписку',
            answer: [
                'Перейдите в раздел «Главная» → нажмите «Купить» → выберите период и количество устройств → нажмите «Продолжить оформление».',
                'Введите промокод, если он у вас есть, выберите способ оплаты и нажмите «Оплатить».',
                'После успешной оплаты подписка автоматически добавится в ваш аккаунт.',
                'Затем нажмите «Подключить VPN» и следуйте инструкциям.'
            ],
            category: 'payment'
        },
        {
            id: 'payment-2',
            question: 'Как посмотреть срок подписки и продлить устройство',
            answer: [
                'Дату окончания подписки можно посмотреть в разделе «Устройства» — она указана у каждого устройства.',
                'Чтобы продлить подписку, перейдите в раздел «Главная», нажмите «Продлить», выберите нужное устройство и срок продления, затем продолжите оформление.',
                'После оплаты срок подписки на устройство будет автоматически продлён.'
            ],
            category: 'payment'
        },
        {
            id: 'payment-3',
            question: 'Оплата не прошла или зависла',
            answer: [
                'Подождите 5-10 минут и проверьте в разделе «Устройства», появилась или продлилась ли подписка.',
                'Если в течение часа изменений нет, обратитесь в поддержку.'
            ],
            category: 'payment'
        },
        {
            id: 'account-1',
            question: 'Как войти, если не работает Telegram',
            answer: [
                'В профиле откройте раздел «Аутентификация» и привяжите email и пароль, если ещё не сделали этого. После этого вы сможете входить в аккаунт через сайт shadownet.nl по email и паролю.',
                'Если вы не хотите привязывать email и пароль, сохраните в профиле ссылку на личный кабинет. По этой ссылке можно входить в аккаунт без авторизации, просто открыв её в браузере.'
            ],
            category: 'account'
        },
        {
            id: 'account-2',
            question: 'Лимит устройств и удаление устройства',
            answer: [
                'Максимальное количество устройств выбирается при оформлении подписки. Список всех подключённых устройств доступен в разделе «Устройства».',
                'Чтобы удалить ненужное устройство, откройте его и нажмите «Удалить устройство».'
            ],
            category: 'account'
        },
        {
            id: 'account-3',
            question: 'Пишет «Подписка отключена» или ограничение по устройствам',
            answer: [
                'Такое сообщение может появиться при превышении лимита устройств или нарушении правил использования.',
                'Удалите ненужные устройства в разделе «Устройства» или оформите новую платную подписку в разделе «Главная» → «Купить».',
                'Если ошибка не исчезает, обратитесь в поддержку.'
            ],
            category: 'account'
        },
        {
            id: 'account-4',
            question: 'Где посмотреть историю платежей',
            answer: 'Все платежи отображаются в профиле, в разделе «История операций».',
            category: 'account'
        },
        {
            id: 'promo-1',
            question: 'Как пригласить друга и получить бонус',
            answer: [
                'Перейдите в профиль, откройте раздел «Реферальная программа» и отправьте другу ссылку-приглашение через Telegram или сайт.',
                'Если друг зарегистрируется по вашей ссылке и оформит подписку, вы будете получать 3 бонусных дня за каждый оплаченный месяц его подписки.',
                'Чтобы использовать бонусные дни, вернитесь в раздел «Реферальная программа», нажмите «Применить бонусные дни», выберите устройство и подтвердите применение.'
            ],
            category: 'promo'
        },
        {
            id: 'promo-2',
            question: 'Как активировать промокод',
            answer: [
                'Перейдите в раздел «Профиль» → «Промокод».',
                'Введите промокод в специальное поле и нажмите «Активировать».',
                'После успешной активации вы увидите сообщение о применении скидки или бонусных дней.',
                'Промокод можно активировать как при оформлении новой подписки, так и в личном кабинете.'
            ],
            category: 'promo'
        },

    ];

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }

        // Имитация загрузки данных страницы (как в SupportPage)
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const toggleItem = (itemId: string) => {
        setOpenItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const filteredItems = selectedCategory === 'all'
        ? faqItems
        : faqItems.filter(item => item.category === selectedCategory);

    const renderAnswer = (answer: string | string[]) => {
        if (Array.isArray(answer)) {
            return (
                <div className="faq-answer-list">
                    {answer.map((line, idx) => (
                        <p key={idx} className="faq-answer-line">{line}</p>
                    ))}
                </div>
            );
        }
        return <p>{answer}</p>;
    };

    // Показываем лоадер во время загрузки
    if (isLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="faq-page loading">
                    <div className="loader"><Loader /></div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="faq-page">
                <div className="faq-page-header">
                    <div className="faq-page-header-block">
                        <button className="faq-back-button" onClick={() => navigate(-1)}>
                            <BackIcon/>
                        </button>
                        <div className="faq-page-title">Часто задаваемые вопросы</div>
                    </div>
                    <div className="faq-page-subtitle">Ответы на популярные вопросы о нашем сервисе</div>
                </div>

                {/* Категории */}
                <div className="faq-categories-section">
                    <div className="faq-categories-grid">
                        {categories.map(category => (
                            <button
                                key={category.id}
                                className={`faq-category-card ${selectedCategory === category.id ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(category.id)}
                            >
                                <span className="faq-category-name">{category.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Список вопросов */}
                <div className="faq-list">
                    {filteredItems.map((item, index) => (
                        <div
                            key={item.id}
                            className={`faq-item ${openItems.includes(item.id) ? 'open' : ''}`}
                            style={{ animationDelay: `${0.2 + (index % 10) * 0.03}s` }}
                        >
                            <div
                                className="faq-question"
                                onClick={() => toggleItem(item.id)}
                            >
                                <span className="faq-question-text">{item.question}</span>
                                <span className="faq-question-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2"
                                              strokeLinecap="round"/>
                                    </svg>
                                </span>
                            </div>
                            <div className="faq-answer">
                                {renderAnswer(item.answer)}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Блок "Не нашли ответ?" */}
                <div className="faq-contact-block">
                    <div className="faq-contact-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h3 className="faq-contact-title">Не нашли ответ?</h3>
                    <p className="faq-contact-text">Свяжитесь с нашей службой поддержки, и мы поможем вам</p>
                    <button
                        className="faq-contact-button"
                        onClick={() => navigate('/support-chat')}
                    >
                        Связаться с поддержкой
                    </button>
                </div>
            </div>
        </div>
    );
}

export default FaqPage;